import { CHECKOUT_ITEM_TYPE_FLIGHT } from 'constants/checkout'

function quantityBaggageReducer(acc: { [id: string]: number }, baggage: App.AddedBaggage) {
  return {
    ...acc,
    [baggage.id]: (acc?.[baggage.id] ?? 0) + baggage.count,
  }
}

function extractBaggageData(pax: App.Passenger, id: string): App.Checkout.BaggageQuantityMap {
  const baggageItems = pax.addedExtras?.baggage?.[id]
  if (!baggageItems) { return {} }

  const selectedBaggageItems = baggageItems.filter(item => item.count > 0)
  return selectedBaggageItems.reduce(quantityBaggageReducer, {})
}

function extractCarryOnBaggageData(pax: App.Passenger, id: string): App.Checkout.BaggageQuantityMap {
  const baggageItems = pax.addedExtras?.carryOnBaggage?.[id]
  if (!baggageItems) { return {} }

  const selectedBaggageItems = baggageItems.filter(item => item.count > 0)
  return selectedBaggageItems.reduce(quantityBaggageReducer, {})
}

type extractFn = (pax: App.Passenger, id: string) => App.Checkout.BaggageQuantityMap
const paxBaggageReducer =
  (journeyId: string, extracter: extractFn) =>
    (acc: App.Checkout.PaxBaggageMap, pax: App.Passenger) => ({ ...acc, [pax.id]: extracter(pax, journeyId) })

/**
 * Extract 'extras' details from the App.Passenger object
 *
 * @remarks
 * The 'extras' object inside the passenger state used in the flight search page serves a dual purpose.
 * For both 'baggage' and 'carryOnBaggage', it:
 *   - Holds data on available options
 *   - Holds data on the selected option(s) (i.e. when element.count > 0)
 *
 * This structure is incompatible with the structure used in the global checkout, so we convert it.
 *   - For the checkout, we only use it to hold the selected baggage options
 */
export function buildExtrasMap(paxList: App.Passenger[], journeyId: string): App.Checkout.FlightItemPaxBaggageMap {
  return {
    baggage: paxList.reduce(paxBaggageReducer(journeyId, extractBaggageData), {}),
    carryOnBaggage: paxList.reduce(paxBaggageReducer(journeyId, extractCarryOnBaggageData), {}),
  }
}

export function isFlightItem(item: App.Checkout.Item): item is App.Checkout.FlightItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_FLIGHT
}
